import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';
import { filter, map, negate } from 'lodash/fp';
import {
  saveSellOrderBanking,
  saveSellOrderOwnersAgreements,
} from '../../sell-process-redux';
import { CUSTOMER_FORM_ID, NEW_BANK } from '../../../../../global/constants';
import { isOrganizationOwner } from '../../sell-process-utils';
import * as SellProcessUtils from '../../sell-process-utils';
import { SellOrderOrganization } from '../../sell-process-utils-types';
import { isPersonSeller } from '../sell-process-form/components/banking/banking-utils';
import { WithBank } from '../sell-process-form/components/banking/banking-types';
import {
  SellOrganizationOwnerForm,
  SellOrganizationSignerForm,
} from '../sell-process-form/components/owners-agreements/owners-agreements-types';
import { SellOrderDetailedApiResponse } from '../../../../../utils/sell-process/sell-process-detailed-types';
import { omitEmptyStrings } from '../../../../../utils/omit-empty-strings';
import { SellOrganizationEntity } from './sell-process-organizations-types';

const sellProcessOrganizationsAdapter = createEntityAdapter<SellOrganizationEntity>({
  selectId: (model) => model.organizationNumber,
});

export const sellProcessOrganizationsSelectors = sellProcessOrganizationsAdapter.getSelectors();

export const sellProcessOrganizations = createSlice({
  name: 'sellOrderOrganizations',
  initialState: {
    [CUSTOMER_FORM_ID]: {
      entities: {},
      ids: [],
    },
  } as Record<string, EntityState<SellOrganizationEntity>>,
  reducers: {
    initSellOrganizations(state, action: PayloadAction<{ id: string }>) {
      state[action.payload.id] = {
        entities: {},
        ids: [],
      };
    },

    saveSellOrderOrganizations(
      state,
      action: PayloadAction<{
        id: string;
        sellOrder: SellOrderDetailedApiResponse;
      }>,
    ) {
      const { sellOrder, id } = action.payload;
      const { organizations } = sellOrder;

      const sellerOrganizations = SellProcessUtils.getSellerOrganizations(organizations);

      const mapOrganization = (organization: SellOrderOrganization) => ({
        organizationAddress: organization.address,
        organizationCity: organization.city,
        organizationName: organization.name,
        organizationNumber: organization.number,
        organizationPostalCode: organization.postalCode,
        organizationId: organization.organizationId,
        bankAccount: organization.bankAccount,
        bankId: organization.bankId,
        signers: map(
          (x) => ({
            signerId: x.signerId,
            firstName: x.firstName,
            lastName: x.lastName,
            fullName: SellProcessUtils.getFullName(x), // TODO check do we need this
            personId: x.personId,
          }),
          organization.signers,
        ),
      });

      sellProcessOrganizationsAdapter.upsertMany(
        state[id],
        map(mapOrganization, sellerOrganizations),
      );
    },
  },
  extraReducers: (builder) => {
    builder.addCase(saveSellOrderOwnersAgreements, (state, action) => {
      const { id, sellers } = action.payload;

      const mapped = sellers
        ?.filter(isOrganizationOwner)
        .map((seller) => {
          const oldSeller = state[id].entities[seller.organizationNumber];

          return {
            organizationAddress: seller.organizationAddress,
            organizationCity: seller.organizationCity,
            organizationName: seller.organizationName,
            organizationNumber: seller.organizationNumber,
            organizationPostalCode: seller.organizationPostalCode,
            organizationId: seller.organizationId,
            signers:
                seller.signers?.map((signer: SellOrganizationSignerForm) => {
                  const oldSigner = oldSeller?.signers.find(
                    ({ signerId }) => signerId === signer.signerId,
                  );
                  const { firstName, lastName } = SellProcessUtils.getNamesFromFullName(
                      signer.fullName as string,
                  );
                  return {
                    ...oldSigner,
                    firstName: signer.firstName ?? firstName,
                    lastName: signer.lastName ?? lastName,
                    signerId: signer.signerId,
                  };
                }) ?? [],
          };
        })
        .map((seller) => omitEmptyStrings(seller)) ?? [];

      sellProcessOrganizationsAdapter.upsertMany(
        state[id],
        mapped as SellOrganizationEntity[],
      );
    });

    builder.addCase(saveSellOrderBanking, (state, action) => {
      const { id, sellers } = action.payload;

      const mapSeller = (seller?: WithBank<SellOrganizationOwnerForm>) => {
        const isAnnanBankSelected = seller?.bankInfo?.id === NEW_BANK;
        return {
          // Need to be `null` specifically so that the backend knows to delete them
          bankId: isAnnanBankSelected ? null : seller?.bankInfo?.id ?? null,
          bankAccount: isAnnanBankSelected ? null : seller?.bankAccount,
          organizationNumber: seller?.organizationNumber,
        };
      };

      const organizations = filter(negate(isPersonSeller), sellers);

      sellProcessOrganizationsAdapter.upsertMany(
        state[id],
        map(mapSeller, organizations) as any,
      );
    });
  },
});

export const sellOrderOrganizationsReducer = sellProcessOrganizations.reducer;
export const { initSellOrganizations, saveSellOrderOrganizations } = sellProcessOrganizations.actions;
